<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import MainLayout from '../components/layouts/MainLayout.vue'

const captureEl = ref<HTMLInputElement | null>(null)
const normalEl = ref<HTMLInputElement | null>(null)

const captureValue = ref<string>('')
const normalValue = ref<string>('')

const lastScan = ref<string>('')

let commitTimer: number | null = null
const COMMIT_AFTER_MS = 120

function scheduleCommit() {
  if (commitTimer) window.clearTimeout(commitTimer)
  commitTimer = window.setTimeout(() => commitScan(), COMMIT_AFTER_MS)
}

function commitScan() {
  const value = (captureValue.value ?? '').trim()
  if (!value) return

  lastScan.value = value
  captureValue.value = ''

  void focusCapture()
}

async function focusCapture() {
  await nextTick()
  const el = captureEl.value
  if (!el) return

  try {
    el.focus({ preventScroll: true } as any)
  } catch {
    el.focus()
  }

  try {
    const len = el.value.length
    el.setSelectionRange(len, len)
  } catch {
    // ignore
  }
}

function decodeKey(e: KeyboardEvent): string | null {
  if (typeof e.key === 'string' && e.key) {
    if (e.key === 'Enter') return 'ENTER'
    if (e.key === 'Tab') return 'TAB'
    if (e.key.length === 1) return e.key
    return null
  }

  const anyE = e as any
  const code: number | undefined = typeof anyE.keyCode === 'number' ? anyE.keyCode : anyE.which
  if (typeof code !== 'number') return null

  if (code === 13) return 'ENTER'
  if (code === 9) return 'TAB'

  if (code >= 48 && code <= 57) return String.fromCharCode(code)
  if (code >= 96 && code <= 105) return String(code - 96)

  if (code >= 65 && code <= 90) {
    const ch = String.fromCharCode(code)
    return e.shiftKey ? ch : ch.toLowerCase()
  }

  // Common barcode chars (extend if needed)
  if (code === 189 || code === 109) return e.shiftKey ? '_' : '-'
  if (code === 190 || code === 110) return '.'
  if (code === 191 || code === 111) return '/'

  return null
}

function onCaptureKeydown(e: KeyboardEvent) {
  const decoded = decodeKey(e)
  if (!decoded) return

  if (decoded === 'ENTER' || decoded === 'TAB') {
    e.preventDefault()
    e.stopPropagation()
    commitScan()
    return
  }

  captureValue.value += decoded
  scheduleCommit()
}

function onCaptureBlur() {
  window.setTimeout(() => {
    const active = document.activeElement
    if (active === normalEl.value) return
    if (active === captureEl.value) return
    void focusCapture()
  }, 0)
}

function onNormalBlur() {
  window.setTimeout(() => void focusCapture(), 0)
}

function onVisibilityChange() {
  if (!document.hidden) void focusCapture()
}

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibilityChange)
  void focusCapture()
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (commitTimer) window.clearTimeout(commitTimer)
})
</script>

<template>
  <MainLayout>
    <div>
      <div class="mb-12">
        <div class="small text-muted mb-4">Last scanned barcode:</div>
        <div class="h5 mb-0">
          <code>{{ lastScan || '—' }}</code>
        </div>
      </div>
      <div class="mb-12">
        <label class="form-label mb-4">Capture input (scanner)</label>
        <input
          ref="captureEl"
          v-model="captureValue"
          class="form-control"
          type="text"
          autocomplete="off"
          spellcheck="false"
          inputmode="none"
          enterkeyhint="done"
          @keydown="onCaptureKeydown"
          @blur="onCaptureBlur"
        />
        <div class="small text-muted mt-4">
          Live capture:
          <code>{{ captureValue || '—' }}</code>
        </div>
      </div>
      <div class="mb-12">
        <label class="form-label mb-4">Normal input</label>
        <input
          ref="normalEl"
          v-model="normalValue"
          class="form-control"
          type="text"
          autocomplete="off"
          spellcheck="false"
          @blur="onNormalBlur"
        />
        <div class="small text-muted mt-4">
          Normal value:
          <code>{{ normalValue || '—' }}</code>
        </div>
      </div>
    </div>
  </MainLayout>
</template>
